import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import {
  ChevronLeft,
  CheckCircle2,
  XCircle,
  Clock,
  UtensilsCrossed,
  Loader2,
} from 'lucide-react';
import { Button } from '@shared/components/ui/button';
import BookingHeader from '@features/booking/components/BookingHeader';
import { SummaryRow } from '@features/booking/components/SummaryRow';
import { BookingChrome } from '@features/booking/layouts';
import BookingFooter from '@features/booking/components/BookingFooter';
import {
  getReservationDataEncrypted,
  postReservationAction,
} from '../api/reservations';
import type { ReservationDataResponse } from '../api/reservations';

type StatusView = 'cancelled' | 'voided' | 'waiting' | 'table_ready' | 'confirmed';

function resolveStatusView(data: ReservationDataResponse): StatusView {
  if (data.cancelled) return 'cancelled';
  if (data.status === 'voided') return 'voided';
  if (data.confirmStatus || data.status === 'confirmed') return 'confirmed';
  if (data.fpStatus === 'table_ready') return 'table_ready';
  if (data.walkIn && data.fpStatus === 'arrived') return 'waiting';
  // Default: show as waiting (reservation exists, no specific action yet)
  return 'waiting';
}

const ReservationStatusPage = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation('booking');

  const [acting, setActing] = useState(false);
  const [actionResult, setActionResult] = useState<'confirmed' | 'cancelled' | null>(null);
  const [actionError, setActionError] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['reservationStatus', token],
    queryFn: () => getReservationDataEncrypted(token!),
    enabled: !!token,
    retry: false,
    meta: { silentOn: [400, 401, 403, 404, 410, 422], source: 'reservation-status-link' },
  });

  const handleAction = async (action: 'confirm' | 'cancel') => {
    if (!token) return;
    setActing(true);
    setActionError(false);
    try {
      await postReservationAction(token, action);
      setActionResult(action === 'confirm' ? 'confirmed' : 'cancelled');
    } catch {
      setActionError(true);
    } finally {
      setActing(false);
    }
  };

  const renderReservationInfo = () => {
    if (!data) return null;
    return (
      <div className="w-full max-w-sm px-4 space-y-4">
        <div className="bg-foreground/5 rounded-xl p-4 space-y-3">
          <SummaryRow label={t('RESERVATION_STATUS_RESTAURANT')} value={data.restaurant.name} />
          <SummaryRow label={t('RESERVATION_STATUS_DATE')} value={data.date} />
          <SummaryRow label={t('RESERVATION_STATUS_TIME')} value={data.time} />
          <SummaryRow label={t('RESERVATION_STATUS_GUESTS')} value={data.partySize} />
        </div>
      </div>
    );
  };

  const renderContent = () => {
    // No token
    if (!token) {
      return (
        <>
          <div className="flex flex-col items-center pt-4">
            <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mb-6">
              <XCircle className="text-red-600" size={32} />
            </div>
            <h1 className="text-3xl font-display mb-2 text-center">{t('RESERVATION_STATUS_INVALID_TITLE')}</h1>
            <p className="text-foreground/50 text-sm mb-8 text-center">{t('RESERVATION_STATUS_INVALID_SUBTITLE')}</p>
          </div>
          <div className="w-full max-w-sm px-4">
            <Button
              className="bg-brand text-brand-foreground font-medium py-6 px-8 rounded-xl w-full hover:bg-brand-dark shadow-md"
              onClick={() => navigate('/')}
            >
              {t('RESERVATION_STATUS_BACK_TO_HOME')}
            </Button>
          </div>
        </>
      );
    }

    // Loading
    if (isLoading) {
      return (
        <div className="text-center text-foreground/40 text-sm py-12">
          {t('RESERVATION_STATUS_LOADING')}
        </div>
      );
    }

    // API error
    if (error || !data) {
      return (
        <>
          <div className="flex flex-col items-center pt-4">
            <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mb-6">
              <XCircle className="text-red-600" size={32} />
            </div>
            <h1 className="text-3xl font-display mb-2 text-center">{t('RESERVATION_STATUS_INVALID_TITLE')}</h1>
            <p className="text-foreground/50 text-sm mb-8 text-center">{t('RESERVATION_STATUS_INVALID_SUBTITLE')}</p>
          </div>
          <div className="w-full max-w-sm px-4">
            <Button
              className="bg-brand text-brand-foreground font-medium py-6 px-8 rounded-xl w-full hover:bg-brand-dark shadow-md"
              onClick={() => navigate('/')}
            >
              {t('RESERVATION_STATUS_BACK_TO_HOME')}
            </Button>
          </div>
        </>
      );
    }

    // Post-action result
    if (actionResult === 'confirmed') {
      return (
        <>
          <div className="flex flex-col items-center pt-4">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-6">
              <CheckCircle2 className="text-green-600" size={32} />
            </div>
            <h1 className="text-3xl font-display mb-2 text-center">{t('WAITING_QUEUE_CONFIRMED_TABLE')}</h1>
            <p className="text-foreground/50 text-sm mb-8 text-center">{t('RESERVATION_STATUS_CONFIRMED_SUBTITLE')}</p>
          </div>
          {renderReservationInfo()}
          <div className="w-full max-w-sm px-4 mt-8">
            <Button
              className="bg-brand text-brand-foreground font-medium py-6 px-8 rounded-xl w-full hover:bg-brand-dark shadow-md"
              onClick={() => navigate('/')}
            >
              {t('RESERVATION_STATUS_BACK_TO_HOME')}
            </Button>
          </div>
        </>
      );
    }

    if (actionResult === 'cancelled') {
      return (
        <>
          <div className="flex flex-col items-center pt-4">
            <div className="w-16 h-16 rounded-full bg-orange-100 flex items-center justify-center mb-6">
              <XCircle className="text-orange-600" size={32} />
            </div>
            <h1 className="text-3xl font-display mb-2 text-center">{t('WAITING_QUEUE_CANCELED_TABLE')}</h1>
            <p className="text-foreground/50 text-sm mb-8 text-center">{t('RESERVATION_STATUS_CANCELLED_SUBTITLE')}</p>
          </div>
          <div className="w-full max-w-sm px-4 mt-8">
            <Button
              className="bg-brand text-brand-foreground font-medium py-6 px-8 rounded-xl w-full hover:bg-brand-dark shadow-md"
              onClick={() => navigate('/')}
            >
              {t('RESERVATION_STATUS_BACK_TO_HOME')}
            </Button>
          </div>
        </>
      );
    }

    // Live status views
    const view = resolveStatusView(data);

    if (view === 'cancelled') {
      return (
        <>
          <div className="flex flex-col items-center pt-4">
            <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mb-6">
              <XCircle className="text-red-600" size={32} />
            </div>
            <h1 className="text-3xl font-display mb-2 text-center">{t('WAITING_QUEUE_CANCELED_TABLE')}</h1>
            <p className="text-foreground/50 text-sm mb-8 text-center">{t('RESERVATION_STATUS_CANCELLED_SUBTITLE')}</p>
          </div>
          <div className="w-full max-w-sm px-4">
            <Button
              className="bg-brand text-brand-foreground font-medium py-6 px-8 rounded-xl w-full hover:bg-brand-dark shadow-md"
              onClick={() => navigate('/')}
            >
              {t('RESERVATION_STATUS_BACK_TO_HOME')}
            </Button>
          </div>
        </>
      );
    }

    if (view === 'voided') {
      return (
        <>
          <div className="flex flex-col items-center pt-4">
            <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mb-6">
              <XCircle className="text-red-600" size={32} />
            </div>
            <h1 className="text-3xl font-display mb-2 text-center">{t('RESERVATION_STATUS_VOIDED_TITLE')}</h1>
            <p className="text-foreground/50 text-sm mb-8 text-center">{t('RESERVATION_STATUS_VOIDED_SUBTITLE')}</p>
          </div>
          <div className="w-full max-w-sm px-4">
            <Button
              className="bg-brand text-brand-foreground font-medium py-6 px-8 rounded-xl w-full hover:bg-brand-dark shadow-md"
              onClick={() => navigate('/')}
            >
              {t('RESERVATION_STATUS_BACK_TO_HOME')}
            </Button>
          </div>
        </>
      );
    }

    if (view === 'confirmed') {
      return (
        <>
          <div className="flex flex-col items-center pt-4">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-6">
              <CheckCircle2 className="text-green-600" size={32} />
            </div>
            <h1 className="text-3xl font-display mb-2 text-center">{t('WAITING_QUEUE_CONFIRMED_TABLE')}</h1>
            <p className="text-foreground/50 text-sm mb-8 text-center">{t('RESERVATION_STATUS_CONFIRMED_SUBTITLE')}</p>
          </div>
          {renderReservationInfo()}
          <div className="w-full max-w-sm px-4 mt-8">
            <Button
              className="bg-brand text-brand-foreground font-medium py-6 px-8 rounded-xl w-full hover:bg-brand-dark shadow-md"
              onClick={() => navigate('/')}
            >
              {t('RESERVATION_STATUS_BACK_TO_HOME')}
            </Button>
          </div>
        </>
      );
    }

    if (view === 'waiting') {
      return (
        <>
          <div className="flex flex-col items-center pt-4">
            <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center mb-6">
              <Clock className="text-blue-600" size={32} />
            </div>
            <h1 className="text-3xl font-display mb-2 text-center">{t('WAITING_QUEUE_WAITING_TABLE')}</h1>
            <p className="text-foreground/50 text-sm mb-8 text-center">{t('RESERVATION_STATUS_WAITING_SUBTITLE')}</p>
          </div>
          {renderReservationInfo()}
          {actionError && (
            <p className="text-red-500 text-sm mt-4 text-center px-4">
              {t('RESERVATION_STATUS_ERROR')}
            </p>
          )}
          <div className="w-full max-w-sm px-4 mt-8">
            <Button
              variant="outline"
              className="font-medium py-6 px-8 rounded-xl w-full border-red-300 text-red-600 hover:bg-red-50"
              onClick={() => handleAction('cancel')}
              disabled={acting}
            >
              {acting ? (
                <Loader2 size={18} className="mr-2 animate-spin" />
              ) : (
                <XCircle size={18} className="mr-2" />
              )}
              {t('WAITING_QUEUE_CANCEL_TABLE')}
            </Button>
          </div>
        </>
      );
    }

    // table_ready
    return (
      <>
        <div className="flex flex-col items-center pt-4">
          <div className="w-16 h-16 rounded-full bg-orange-100 flex items-center justify-center mb-6">
            <UtensilsCrossed className="text-orange-600" size={32} />
          </div>
          <h1 className="text-3xl font-display mb-2 text-center">{t('WAITING_QUEUE_TABLE_READY')}</h1>
          <p className="text-foreground/50 text-sm mb-8 text-center">{t('WAITING_QUEUE_NOTIFICATION_2')}</p>
        </div>
        {renderReservationInfo()}
        {actionError && (
          <p className="text-red-500 text-sm mt-4 text-center px-4">
            {t('RESERVATION_STATUS_ERROR')}
          </p>
        )}
        <div className="w-full max-w-sm px-4 mt-8 flex flex-col gap-3">
          <Button
            className="bg-brand text-brand-foreground font-medium py-6 px-8 rounded-xl w-full hover:bg-brand-dark shadow-md"
            onClick={() => handleAction('confirm')}
            disabled={acting}
          >
            {acting ? (
              <Loader2 size={18} className="mr-2 animate-spin" />
            ) : (
              <CheckCircle2 size={18} className="mr-2" />
            )}
            {t('WAITING_QUEUE_CONFIRM')}
          </Button>
          <Button
            variant="outline"
            className="font-medium py-6 px-8 rounded-xl w-full border-red-300 text-red-600 hover:bg-red-50"
            onClick={() => handleAction('cancel')}
            disabled={acting}
          >
            <XCircle size={18} className="mr-2" />
            {t('WAITING_QUEUE_CANCEL_TABLE')}
          </Button>
        </div>
      </>
    );
  };

  return (
    <BookingChrome flexCol>
        <BookingHeader />

        <main className="booking-content pt-8 pb-10 flex flex-col items-center flex-1">
          {renderContent()}
        </main>

        <footer className="border-t border-border">
          <div className="flex items-center px-6 py-4">
            <button
              aria-label="Go back"
              className="w-10 h-10 rounded-full border border-brand text-brand bg-card flex items-center justify-center hover:bg-brand/10 transition-colors cursor-pointer group shadow-sm"
              onClick={() => navigate('/')}
              type="button"
            >
              <ChevronLeft size={20} className="group-hover:scale-110 transition-transform" />
            </button>
          </div>
          <BookingFooter />
        </footer>
    </BookingChrome>
  );
};

export default ReservationStatusPage;
